// src/contextAssessment.js

import React, { useEffect, useState, useMemo } from 'react';
import { authFetch } from './utils/authFetch';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  Title,
  Text,
  Group,
  Box,
  Stack,
  Skeleton,
  Select,
  TextInput,
  Textarea,
  Button,
  ActionIcon,
  Tooltip,
  MultiSelect,
  Badge,
  List,
  Transition,
} from '@mantine/core';
import { IconEdit, IconCheck, IconHelpCircle } from '@tabler/icons-react';
import AIGeneratingIcon from './components/AIGeneratingIcon';
import {
  setContextData,
  updateContextData,
  generateUnifiedNarrative,
  saveContextData,
  fetchContextData,
} from './redux/actions/contextActions';
import debounce from 'lodash/debounce';

function toTitleCase(str) {
  // Optionally keep the replace line for legacy reasons, but the map below handles acronyms case-insensitively.
  // str = str.replace(/\b(EU|ROC|AUC)\b/g, (m) => m);
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\b([Aa]nd)\b/g, '&')
    .split(/\s+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (['EU', 'ROC', 'AUC'].includes(upper)) {
        return upper;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

export default function ContextAssessment({ modelId, assessmentUuid, sectionName }) {
  const dispatch = useDispatch();
  const config = useSelector((s) => s.config.configs['contextForm']);
  const configLoading = useSelector((s) => s.config.loading);
  const configError = useSelector((s) => s.config.error);
  const contextData = useSelector((s) => s.context.data[modelId] || {});
  const modelMeta = useSelector((s) => s.risks.byAssessment[modelId] || {});
  const isLoading = useSelector((s) => s.context.loading);
  // const riskFormData = useSelector((s) => s.risks.models[modelId]?.riskFormData || {});

  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState({});
  const [editingUnifiedNarrative, setEditingUnifiedNarrative] = useState(false);
  const [unifiedNarrativeText, setUnifiedNarrativeText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const sections = useMemo(() => (config ? Object.keys(config) : []), [config]);
  const labels = sections.map(toTitleCase);
  const sec = sections[activeTab];

  const [hoverStates, setHoverStates] = useState({});
  const [showNarrative, setShowNarrative] = useState(!!contextData[sec]?.narrative);

  useEffect(() => {
    if (contextData[sec]?.narrative) {
      setShowNarrative(true);
    } else {
      setShowNarrative(false);
    }
  }, [contextData, sec]);

  // Fetch context data when component mounts or when modelId/assessmentUuid changes
  useEffect(() => {
    if (modelId && assessmentUuid) {
      dispatch(fetchContextData(modelId, assessmentUuid));
    }
  }, [dispatch, modelId, assessmentUuid]);

  useEffect(() => {
    if (sectionName && sections.includes(sectionName)) {
      setActiveTab(sections.indexOf(sectionName));
    }
  }, [sectionName, sections]);

  useEffect(() => {
    if (config && modelId) {
      const initial = sections.reduce((acc, sec) => {
        acc[sec] = config[sec].reduce((fAcc, f) => {
          fAcc[f.name] = f.type === 'multiselect' ? [] : f.defaultValue || '';
          return fAcc;
        }, {});
        return acc;
      }, {});
      
      // Only initialize if no context data exists yet
      if (!contextData || Object.keys(contextData).length === 0) {
        if (initial.modelContext) {
          initial.modelContext['Model Type'] = modelMeta?.modelType || '';
          initial.modelContext['Model Stage'] = modelMeta?.modelStage || '';
          if (initial.useCase) {
            initial.useCase['Use Case Description'] = modelMeta?.useCaseShortDescription || '';
          }
        }
        dispatch(setContextData(modelId, initial));
      }
    }
  }, [config, dispatch, modelId, sections, contextData, modelMeta?.modelType, modelMeta?.modelStage, modelMeta?.useCaseShortDescription]);

  const debouncedSave = useMemo(
    () =>
      debounce((sec, data) => {
        setIsSaving(true);
        dispatch(saveContextData(modelId, assessmentUuid, sec, data))
          .then(() => {
            setIsSaving(false);
            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 2000);
          })
          .catch(() => setIsSaving(false));
      }, 1500),
    [dispatch, modelId, assessmentUuid]
  );

  const handleChange = (sec, name, value) => {
    dispatch(updateContextData(modelId, sec, name, value));
    debouncedSave(sec, { ...contextData[sec], [name]: value });
  };


  // New unified generation function
  const handleGenerateUnified = () => {
    dispatch(generateUnifiedNarrative(modelId, assessmentUuid, contextData));
  };

  // Unified narrative editing functions
  const handleEditUnifiedNarrative = () => {
    setUnifiedNarrativeText(contextData.unifiedNarrative?.narrative || '');
    setEditingUnifiedNarrative(true);
  };

  const handleSaveUnifiedNarrative = async () => {
    try {
      // Save to database via API call
      const response = await authFetch('/.netlify/functions/saveUnifiedNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          narrative: unifiedNarrativeText,
          keyCharacteristics: contextData.unifiedNarrative?.keyCharacteristics || [],
          confidence: contextData.unifiedNarrative?.confidence || 0,
          missingInfo: contextData.unifiedNarrative?.missingInfo || []
        })
      });

      if (!response.ok) throw new Error('Failed to save narrative');

      // Update Redux state
      dispatch({
        type: 'FETCH_UNIFIED_NARRATIVE_SUCCESS',
        payload: {
          modelId,
          narrative: unifiedNarrativeText,
          keyCharacteristics: contextData.unifiedNarrative?.keyCharacteristics || [],
          confidence: contextData.unifiedNarrative?.confidence || 0,
          missingInfo: contextData.unifiedNarrative?.missingInfo || []
        }
      });
      setEditingUnifiedNarrative(false);
    } catch (error) {
      console.error('Error saving unified narrative:', error);
      // TODO: Show error message to user
    }
  };

  const handleCancelEditUnifiedNarrative = () => {
    setEditingUnifiedNarrative(false);
    setUnifiedNarrativeText('');
  };

  // Check if all sections have sufficient data for unified generation
  const isReadyForUnifiedGeneration = useMemo(() => {
    const hasUseCase = contextData.useCase && Object.keys(contextData.useCase).length > 0;
    const hasModelContext = contextData.modelContext && Object.keys(contextData.modelContext).length > 0;
    const hasBusinessContext = contextData.businessContext && Object.keys(contextData.businessContext).length > 0;
    return hasUseCase && hasModelContext && hasBusinessContext;
  }, [contextData]);

  // Check if unified narrative exists
  const hasUnifiedNarrative = useMemo(() => {
    return contextData.unifiedNarrative && contextData.unifiedNarrative.narrative && contextData.unifiedNarrative.narrative.trim().length > 0;
  }, [contextData]);

  const toggleEdit = (sec) => {
    setEditMode((e) => ({ ...e, [sec]: !e[sec] }));
  };


  if (configLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}
      >
        <Stack spacing="md" sx={{ width: 500 }}>
          <Skeleton height={32} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={40} radius="md" />
        </Stack>
      </Box>
    );
  }
  if (configError) {
    return (
      <Box p="xl">
        <Text c="red" weight={600}>
          Error loading configuration: {configError}
        </Text>
      </Box>
    );
  }


  return (
    <Box px="xl" py="lg" sx={{ maxWidth: 820, mx: 'auto' }}>
      {/* Header */}
      <Group position="apart" mb="md">
        <Group>
          <Title order={4} weight={600}>
            {labels[activeTab] || <Skeleton width={120} />}
          </Title>
          {isLoading && <AIGeneratingIcon isGenerating size={20} />}
          <Badge color={isSaving ? 'yellow' : isSaved ? 'green' : 'gray'} variant="light">
            {isSaving ? 'Saving…' : isSaved ? 'Saved' : 'Idle'}
          </Badge>
        </Group>
      </Group>

      {/* Fields and Narrative Side by Side */}
      <Group spacing="lg" align="stretch" style={{ flexWrap: 'wrap' }}>
        {/* Fields */}
        <Box style={{ flex: showNarrative ? 1 : '100%' }}>
          <Card
            withBorder
            radius="md"
            p="xl"
            sx={{ background: '#f9fafb' }}
            style={{ flex: 1, minWidth: 300, textAlign: 'left' }}
          >
            <Stack>
              {config[sec].map((field) => {
                return (
                  <Box
                    key={field.name}
                    onMouseEnter={() => setHoverStates((h) => ({ ...h, [field.name]: true }))}
                    onMouseLeave={() => setHoverStates((h) => ({ ...h, [field.name]: false }))}
                    style={{ position: 'relative', textAlign: 'left' }}
                  >
                    {(() => {
                      const value = contextData[sec]?.[field.name] ?? (field.type === 'multiselect' ? [] : '');
                      const tooltipIcon = (
                        <Tooltip label={field.description || 'No info'} withArrow>
                          <ActionIcon size="sm" variant="subtle">
                            <IconHelpCircle size={16} />
                          </ActionIcon>
                        </Tooltip>
                      );
                      if (field.type === 'dropdown') {
                        return (
                          <Select
                            label={
                              <Group spacing="xs" align="center">
                                <Text weight={500}>{toTitleCase(field.name)}</Text>
                                {hoverStates[field.name] && tooltipIcon}
                              </Group>
                            }
                            data={field.options || []}
                            value={value}
                            onChange={(v) => handleChange(sec, field.name, v)}
                            mb="xs"
                            placeholder={`Select ${toTitleCase(field.name)}`}
                          />
                        );
                      }
                      if (field.type === 'multiselect') {
                        return (
                          <MultiSelect
                            label={
                              <Group spacing="xs" align="center">
                                <Text weight={500}>{toTitleCase(field.name)}</Text>
                                {hoverStates[field.name] && tooltipIcon}
                              </Group>
                            }
                            data={field.options || []}
                            value={value}
                            onChange={(v) => handleChange(sec, field.name, v)}
                            mb="xs"
                            placeholder={`Select ${toTitleCase(field.name)}`}
                          />
                        );
                      }
                      if (field.type === 'textarea') {
                        return (
                          <Textarea
                            label={
                              <Group spacing="xs" align="center">
                                <Text weight={500}>{toTitleCase(field.name)}</Text>
                                {hoverStates[field.name] && tooltipIcon}
                              </Group>
                            }
                            value={value}
                            onChange={(e) => handleChange(sec, field.name, e.currentTarget.value)}
                            autosize
                            minRows={3}
                            mb="xs"
                            placeholder={`Enter ${toTitleCase(field.name)}`}
                          />
                        );
                      }
                      return (
                        <TextInput
                          label={
                            <Group spacing="xs" align="center">
                              <Text weight={500}>{toTitleCase(field.name)}</Text>
                              {hoverStates[field.name] && tooltipIcon}
                            </Group>
                          }
                          value={value}
                          onChange={(e) => handleChange(sec, field.name, e.currentTarget.value)}
                          mb="xs"
                          placeholder={`Enter ${toTitleCase(field.name)}`}
                        />
                      );
                    })()}
                  </Box>
                );
              })}
            </Stack>
          </Card>
        </Box>



        {/* Narrative (slide in) */}
        <Transition mounted={showNarrative} transition="fade-left" duration={300} timingFunction="ease" key={sec}>
          {(styles) => (
            <Card
              withBorder
              radius="md"
              p="xl"
              sx={{ background: '#f9fafb' }}
              style={{ flex: 1, minWidth: 300, ...styles }}
            >
              <Group position="apart" mb="sm">
                <Group>
                  <Title order={5} weight={600}>
                    Narrative for {toTitleCase(sec)}
                  </Title>
                </Group>
                {contextData[sec]?.narrative && (
                  <Tooltip label={editMode[sec] ? 'Save changes' : 'Edit narrative'} withArrow>
                    <ActionIcon onClick={() => toggleEdit(sec)}>
                      {editMode[sec] ? <IconCheck size={18} /> : <IconEdit size={18} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              {editMode[sec] ? (
                <Textarea
                  value={contextData[sec]?.narrative || ''}
                  onChange={(e) => updateContextData(modelId, sec, 'narrative', e.currentTarget.value)}
                  autosize
                  minRows={4}
                  mb="sm"
                  placeholder="Enter narrative…"
                />
              ) : (
                contextData[sec]?.narrative && (
                  <Text style={{ whiteSpace: 'pre-wrap', padding: '12px', background: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    {contextData[sec].narrative}
                  </Text>
                )
              )}
            </Card>
          )}
        </Transition>
      </Group>

      {/* Unified Context Narrative Generation */}
      {isReadyForUnifiedGeneration && (
        <Card withBorder radius="md" p="xl" mt="xl" sx={{ background: '#f0f8ff' }}>
          <Stack spacing="md">
            <Group position="apart" align="center">
              <div>
                <Title order={4} color="blue.7">🎯 Complete Model Narrative</Title>
                <Text size="sm" color="dimmed">
                  Generate a comprehensive narrative from all context sections
                </Text>
              </div>
              <Badge color={isLoading ? 'blue' : hasUnifiedNarrative ? 'green' : 'gray'} variant="light">
                {isLoading ? 'Generating...' : hasUnifiedNarrative ? 'Generated' : 'Ready to Generate'}
              </Badge>
            </Group>

            {!hasUnifiedNarrative ? (
              <Group position="left" py="md">
                <Button
                  size="lg"
                  color="blue"
                  leftSection={<AIGeneratingIcon isGenerating={isLoading} size={18} color="white" />}
                  onClick={handleGenerateUnified}
                  disabled={isLoading}
                  variant="filled"
                >
                  {isLoading ? 'Generating Complete Narrative...' : 'Generate Complete Model Narrative'}
                </Button>
              </Group>
            ) : (
              <div>
                <Group position="apart" align="center" mb="sm">
                  <Text weight={600} size="sm" color="blue.7">Generated Narrative</Text>
                  <Group spacing="xs">
                    {contextData.unifiedNarrative?.confidence && (
                      <Badge size="xs" color="green" variant="light">
                        {Math.round(contextData.unifiedNarrative.confidence * 100)}% confidence
                      </Badge>
                    )}
                    <Button
                      size="xs"
                      variant="light"
                      color="gray"
                      leftSection={<IconEdit size={14} />}
                      onClick={handleEditUnifiedNarrative}
                      disabled={isLoading || editingUnifiedNarrative}
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      leftSection={<AIGeneratingIcon isGenerating={isLoading} size={14} />}
                      onClick={handleGenerateUnified}
                      disabled={isLoading || editingUnifiedNarrative}
                    >
                      {isLoading ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                  </Group>
                </Group>
                
                {/* Key Characteristics - MOVED TO TOP */}
                {contextData.unifiedNarrative?.keyCharacteristics && (
                  <Box mb="md" pb="md" sx={{ borderBottom: '1px solid #f1f3f4' }}>
                    <Text size="sm" weight={500} mb="xs" color="blue.7" align="left">Key Characteristics:</Text>
                    <Group spacing="xs" position="left">
                      {contextData.unifiedNarrative.keyCharacteristics.map((char, idx) => (
                        <Badge key={idx} size="sm" variant="light" color="blue">
                          {char}
                        </Badge>
                      ))}
                    </Group>
                  </Box>
                )}

                {/* Main Narrative Content */}
                {editingUnifiedNarrative ? (
                  <Stack spacing="sm">
                    <Textarea
                      value={unifiedNarrativeText}
                      onChange={(e) => setUnifiedNarrativeText(e.target.value)}
                      minRows={10}
                      placeholder="Edit the unified narrative..."
                      sx={{
                        fontSize: '14px',
                        lineHeight: 1.6,
                        '& .mantine-Textarea-input': {
                          textAlign: 'left'
                        }
                      }}
                    />
                    <Group spacing="xs" position="left">
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCheck size={14} />}
                        onClick={handleSaveUnifiedNarrative}
                      >
                        Save
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        onClick={handleCancelEditUnifiedNarrative}
                      >
                        Cancel
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <Box
                    p="md"
                    mb="md"
                    sx={{
                      background: 'white',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      borderBottom: '1px solid #f1f3f4'
                    }}
                  >
                    <Text style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }} align="left">
                      {contextData.unifiedNarrative?.narrative || 'No narrative available'}
                    </Text>
                  </Box>
                )}

                {/* Missing Information - KEPT AT BOTTOM */}
                {contextData.unifiedNarrative?.missingInfo && contextData.unifiedNarrative.missingInfo.length > 0 && (
                  <Box pt="md" sx={{ borderTop: '1px solid #f1f3f4' }}>
                    <Text size="sm" weight={500} mb="xs" color="orange.7" align="left">Missing Information:</Text>
                    <List size="sm" spacing="xs" sx={{ '& .mantine-List-item': { textAlign: 'left' } }}>
                      {contextData.unifiedNarrative.missingInfo.map((info, idx) => (
                        <List.Item key={idx} style={{ color: '#d9480f', textAlign: 'left' }}>{info}</List.Item>
                      ))}
                    </List>
                  </Box>
                )}
              </div>
            )}
          </Stack>
        </Card>
      )}
    </Box>
  );
}
